/**
 * Account block — the wire contract between the server, the web app's
 * suspended screen, and the admin panel. Spec:
 * docs/superpowers/specs/2026-09-17-block-user-design.md
 */

export const ACCOUNT_BLOCK_REASONS = ['security', 'terms', 'payment', 'other'] as const;
export type AccountBlockReason = (typeof ACCOUNT_BLOCK_REASONS)[number];

/** The one refusal code every door returns for a blocked account. */
export type AccountBlockErrorCode = 'account_blocked';

/** What the person sees for each category. The admin's note is never shown. */
export const ACCOUNT_BLOCK_REASON_LABELS: Record<AccountBlockReason, string> = {
  security: 'Security',
  terms: 'Breaking our terms of use',
  payment: 'Payment issue',
  other: 'Other',
};

export type AccountBlockStatus =
  | { blocked: false }
  | {
      blocked: true;
      /** ISO timestamp of the block. */
      since: string;
      reason: AccountBlockReason;
      reference: string;
      email: string;
    };

export const ACCOUNT_SUPPORT_EMAIL = 'user@vestra.ai';

const REFERENCE_PATTERN = /^VS-([0-9A-F]{4})-([0-9A-F]{4})$/i;

/** `VS-` + the first 8 hex characters of the id, split 4-4. No table behind it. */
export function accountReference(userId: string): string {
  const hex = userId.replace(/-/g, '').slice(0, 8).toUpperCase();
  return `VS-${hex.slice(0, 4)}-${hex.slice(4, 8)}`;
}

/** The lower-case id prefix a reference stands for, or null when it is not one. */
export function referenceToIdPrefix(input: string): string | null {
  const m = REFERENCE_PATTERN.exec(input.trim());
  return m ? `${m[1]}${m[2]}`.toLowerCase() : null;
}

/** The one reply a blocked person gets on WhatsApp / iMessage / Telegram. */
export function blockNoticeText(reference: string): string {
  return `This Vestra account is suspended. If you think this is a mistake, email ${ACCOUNT_SUPPORT_EMAIL} with reference ${reference}.`;
}

export interface AdminUserBlockEvent {
  action: 'block' | 'unblock';
  reason: AccountBlockReason | null;
  note: string | null;
  actorEmail: string;
  createdAt: string;
}

export interface AdminUserBlockView {
  blocked: boolean;
  blockedAt: string | null;
  reason: AccountBlockReason | null;
  reference: string;
  /** Any plan other than free: the block dialog warns that billing is untouched. */
  hasPaidPlan: boolean;
  /** Newest first. */
  history: AdminUserBlockEvent[];
}
